import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { defer, forkJoin, iif, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { UserUpdate } from "src/app/backend/models/user/user.update";
import { UserNotify } from "src/app/support/notify-decorator";
import { SoftwareProduct } from "src/app/support/product";
import { environment } from "src/environments/environment";
import { Member, MemberScope } from "src/app/backend/models/member/member";
import { MemberAuth } from "src/app/backend/models/member/member-auth";
import {
  Account,
  Domain
} from "src/app/backend/models/organization/organization";
import { Tenant } from "src/app/backend/models/tenant/tenant";
import { User } from "src/app/backend/models/user/user";
import { UserCreate } from "src/app/backend/models/user/user.create";
import { UiSettings } from "src/app/backend/models/settings/ui-settings";
import { omitFalsyProperties } from "src/app/support/object";
import { PaginatedResult } from "src/app/support/paginated-result";

@Injectable()
export class UserBackend {
  constructor(private http: HttpClient) {}

  create(request: UserCreate): Observable<User> {
    return this.http.post<User>(`${environment.snoots.api.iam}/users`, {});
  }

  isActive(): Observable<boolean> {
    return of(true);
  }

  loadAccount(id: string): Observable<Account> {
    return this.http.get<Account>(
      `${environment.snoots.api.organization}/accounts/${id}`
    );
  }

  loadPublicAccount(id: string): Observable<Account> {
    return this.http.get<Account>(
      `${environment.snoots.api.organization}/public/accounts/${id}`
    );
  }

  loadAccounts(slug: string): Observable<Account[]> {
    return this.http
      .get<PaginatedResult<Account>>(
        `${environment.snoots.api.organization}/public/accounts`,
        {
          params: {
            slug
          }
        }
      )
      .pipe(map((response) => response.docs));
  }

  loadPublicTenant(id: string): Observable<Tenant> {
    return this.http.get<any>(
      `${environment.snoots.api.organization}/public/tenants/${id}`
    );
  }

  loadTenant(id: string): Observable<Tenant> {
    return this.http.get<any>(
      `${environment.snoots.api.organization}/tenants/${id}`
    );
  }

  updateTenantProfilePicture(
    tenantId: string,
    storageId: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/tenants/${tenantId}`,
      {
        logo: storageId
      }
    );
  }

  listTenants(): Observable<PaginatedResult<Tenant>> {
    return this.http.get<PaginatedResult<Tenant>>(
      `${environment.snoots.api.organization}/tenants`
    );
  }

  listTenantsForLanding({
    token
  }: {
    token: string;
  }): Observable<PaginatedResult<Tenant>> {
    return this.http.get<PaginatedResult<Tenant>>(
      `${environment.snoots.api.organization}/tenants`,
      {
        headers: {
          member: `Bearer ${token}`
        }
      }
    );
  }

  loadMe(): Observable<User> {
    return this.http
      .get<User>(`${environment.snoots.api.iam}/users/me`)
      .pipe(map(extend));
  }

  loadMembers(account?: string): Observable<MemberAuth[]> {
    return this.http.get<any[]>(
      `${environment.snoots.api.iam}/users/me/members`,
      {
        params: omitFalsyProperties({
          account
        })
      }
    );
  }

  loadMembersByScope(scope: MemberScope): Observable<MemberAuth[]> {
    return this.http.get<any[]>(
      `${environment.snoots.api.iam}/users/me/members`,
      {
        params: omitFalsyProperties({
          scope
        })
      }
    );
  }

  loadMemberByProduct({
    product
  }: {
    product: SoftwareProduct;
  }): Observable<MemberAuth | undefined> {
    let mappedProduct: string;

    switch (product) {
      case SoftwareProduct.PLATFORM: {
        mappedProduct = "portal";
        break;
      }
      default: {
        mappedProduct = product.toLowerCase();
        break;
      }
    }

    return this.http
      .get<any[]>(`${environment.snoots.api.iam}/users/me/members`)
      .pipe(
        switchMap((membersAuth: MemberAuth[]) => {
          const membersAuthValid: MemberAuth[] = membersAuth.filter(
            (memberAuth: MemberAuth) => !!memberAuth.tenant
          );
          return iif(
            () => {
              return !membersAuthValid || membersAuthValid.length === 0;
            },
            of(undefined),
            defer(() => {
              return forkJoin(
                membersAuthValid.map((memberAuth: MemberAuth) => {
                  return this.loadTenant(memberAuth.tenant).pipe(
                    map((tenant: Tenant) => {
                      return {
                        tenant,
                        memberAuth
                      };
                    })
                  );
                })
              );
            })
          );
        }),
        map(
          (
            membersWithTenants:
              | Array<{
                  memberAuth: MemberAuth;
                  tenant: Tenant;
                }>
              | undefined
          ) => {
            if (!membersWithTenants) {
              return undefined;
            }

            return membersWithTenants.find(
              ({ tenant }) => tenant.type.toLowerCase() === mappedProduct
            )?.memberAuth;
          }
        )
      );
  }

  loadMemberByDomain({
    domain
  }: {
    domain: string;
  }): Observable<MemberAuth | undefined> {
    return this.http
      .get<any[]>(`${environment.snoots.api.iam}/users/me/members`)
      .pipe(
        switchMap((membersAuth: MemberAuth[]) => {
          const membersAuthValid: MemberAuth[] = membersAuth.filter(
            (memberAuth: MemberAuth) => !!memberAuth.account
          );
          return iif(
            () => {
              return !membersAuthValid || membersAuthValid.length === 0;
            },
            of(undefined),
            defer(() => {
              return forkJoin(
                membersAuthValid.map((memberAuth: MemberAuth) => {
                  return this.loadPublicAccount(memberAuth.account).pipe(
                    map((account: Account) => {
                      return {
                        account,
                        memberAuth
                      };
                    })
                  );
                })
              );
            })
          );
        }),
        map(
          (
            membersWithAccounts:
              | Array<{
                  memberAuth: MemberAuth;
                  account: Account;
                }>
              | undefined
          ) => {
            if (!membersWithAccounts) {
              return undefined;
            }

            return membersWithAccounts.find(
              ({ memberAuth, account }) => account.domain?.slug === domain
            )?.memberAuth;
          }
        )
      );
  }

  refreshMembers(account: string): Observable<MemberAuth[]> {
    return this.http.get<any[]>(
      `${environment.snoots.api.iam}/users/me/members`,
      {
        headers: {
          ["x-request-refresh-member-token"]: "true"
        },
        params: {
          account
        }
      }
    );
  }

  loadGroups(): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.snoots.api.iam}/members/me/groups`
    );
  }

  acceptInvite(inviteId: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.iam}/members/${inviteId}`,
      {}
    );
  }

  loadDomain(id: string): Observable<Domain> {
    return this.http.get<Domain>(
      `${environment.snoots.api.organization}/domains/${id}`
    );
  }

  loadMemberMe(): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/me`);
  }

  @UserNotify({
    successMessage: marker("Profilo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il profilo")
  })
  updateMe(request: Partial<UserUpdate>): Observable<string> {
    return this.http.patch<string>(
      `${environment.snoots.api.iam}/users/me`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Firma aggiornata"),
    errorMessage: marker("Impossibile aggiornare la firma")
  })
  updateSignature(signature: string): Observable<void> {
    return this.http.patch<void>(`${environment.snoots.api.iam}/users/me`, {
      signature
    });
  }

  @UserNotify({
    successMessage: marker("Profilo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il profilo")
  })
  updateTenant(id: string, request: Partial<Tenant>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/tenants/${id}`,
      request
    );
  }

  updateUiSettings(uiSettings: UiSettings): Observable<void> {
    return this.http.patch<void>(`${environment.snoots.api.iam}/users/me`, {
      uiSettings: JSON.stringify(uiSettings)
    });
  }

  @UserNotify({
    successMessage: marker("Blocco automatico attivato"),
    errorMessage: marker("Impossibile attivare il blocco automatico")
  })
  enableDynamicLock(): Observable<void> {
    return this.http.patch<void>(`${environment.snoots.api.iam}/users/me`, {
      dynamicLockEnabled: true
    });
  }

  @UserNotify({
    successMessage: marker("Blocco automatico disattivato"),
    errorMessage: marker("Impossibile disattivare il blocco automatico")
  })
  disableDynamicLock(): Observable<void> {
    return this.http.patch<void>(`${environment.snoots.api.iam}/users/me`, {
      dynamicLockEnabled: false
    });
  }

  generateOtp(): Observable<{ otp: string; salt: string }> {
    return this.http.post<{ otp: string; salt: string }>(
      `${environment.snoots.api.iam}/users/me/otps`,
      {}
    );
  }
}

function extend(user: User): User {
  return {
    ...user,
    uiSettings: user.uiSettings
      ? JSON.parse(<string>(<unknown>user.uiSettings))
      : {}
  };
}
